import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, X, CheckCircle2, XCircle, ShieldCheck, Mail, Smartphone } from 'lucide-react';
import { initMetaPixel, trackPageView, trackViewContent, trackCheckoutClick } from './utils/metaPixel';
const BUY_LINK = 'https://impulsodigital.vip/cart/44687883272278:1';
const handleCheckoutClick = () => {
  trackCheckoutClick();
};
const CTAButton = ({
  text = '👉 QUIERO MIS 1000 RECETAS AHORA',
  className = ''
}) => <motion.a href={BUY_LINK} onClick={handleCheckoutClick} animate={{
  scale: [1, 1.03, 1]
}} transition={{
  repeat: Infinity,
  duration: 2.5,
  ease: 'easeInOut'
}} className={`block w-full max-w-2xl mx-auto bg-[#16a34a] hover:bg-[#15803d] text-white font-black text-xl md:text-3xl py-5 px-4 md:px-8 rounded-xl shadow-[0_8px_0_rgb(20,83,45)] hover:shadow-[0_4px_0_rgb(20,83,45)] hover:translate-y-[4px] transition-all text-center uppercase tracking-tight border-2 border-green-800 leading-tight ${className}`} data-id="element-0">
    {text}
  </motion.a>;
const Highlight = ({
  children
}: {
  children: React.ReactNode;
}) => <span className="bg-yellow-300 text-black px-2 py-1 rounded-sm font-bold" data-id="element-1">
    {children}
  </span>;
const PurchasePopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [purchaseData, setPurchaseData] = useState({
    name: '',
    country: '',
    time: ''
  });
  const names = ['María', 'Laura', 'Carla', 'Sofía', 'Valentina', 'Camila', 'Lucía', 'Florencia', 'Julieta', 'Martina'];
  const countries = ['Argentina', 'Chile', 'Uruguay', 'Colombia', 'México', 'España', 'Perú'];
  const times = ['hace 2 minutos', 'hace 5 minutos', 'hace 12 minutos', 'hace 1 hora', 'hace 30 minutos'];
  useEffect(() => {
    const showPopup = () => {
      const randomName = names[Math.floor(Math.random() * names.length)];
      const randomCountry = countries[Math.floor(Math.random() * countries.length)];
      const randomTime = times[Math.floor(Math.random() * times.length)];
      setPurchaseData({
        name: randomName,
        country: randomCountry,
        time: randomTime
      });
      setIsVisible(true);
      setTimeout(() => {
        setIsVisible(false);
      }, 5000);
    };
    // Initial delay
    const initialTimer = setTimeout(showPopup, 3000);
    // Recurring
    const interval = setInterval(() => {
      showPopup();
    }, 15000);
    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
  }, []);
  return <AnimatePresence data-id="element-2">
      {isVisible && <motion.div initial={{
      opacity: 0,
      y: 50,
      x: -20
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} exit={{
      opacity: 0,
      y: 20
    }} className="fixed bottom-4 left-4 z-50 bg-white border border-gray-200 shadow-2xl rounded-lg p-4 flex items-start gap-4 max-w-xs" data-id="element-3">
          <button onClick={() => setIsVisible(false)} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600" data-id="element-4">
            <X size={14} data-id="element-5" />
          </button>
          <div className="bg-green-100 p-2 rounded-full mt-1" data-id="element-6">
            <CheckCircle2 className="text-green-600" size={20} data-id="element-7" />
          </div>
          <div data-id="element-8">
            <p className="text-sm text-gray-800" data-id="element-9">
              <span className="font-bold" data-id="element-10">{purchaseData.name}</span> de{' '}
              {purchaseData.country}
            </p>
            <p className="text-xs text-gray-600 font-medium mt-1" data-id="element-11">
              Compró "Bajá de peso comiendo así"
            </p>
            <p className="text-xs text-gray-400 mt-1" data-id="element-12">{purchaseData.time}</p>
          </div>
        </motion.div>}
    </AnimatePresence>;
};
export function App() {
  useEffect(() => {
    initMetaPixel();
    trackPageView();
    trackViewContent();
  }, []);
  return <div className="min-h-screen bg-gray-50 font-sans text-gray-900 selection:bg-yellow-300 selection:text-black pb-20" data-id="element-13">
      {/* HERO SECTION */}
      <section className="bg-white pt-12 pb-16 px-4 border-b-4 border-gray-200" data-id="element-14">
        <div className="max-w-4xl mx-auto text-center" data-id="element-15">
          <div className="inline-block bg-yellow-100 text-yellow-800 font-bold px-4 py-1 rounded-full mb-6 text-sm md:text-base border border-yellow-300" data-id="element-16">
            🟡 OFERTA ESPECIAL
          </div>

          <h1 className="text-4xl md:text-6xl font-black leading-tight mb-8 text-gray-900 tracking-tight" data-id="element-17">
            👉 Bajá de peso comiendo así todos los días{' '}
            <br className="hidden md:block" data-id="element-18" />
            <span className="text-red-600" data-id="element-19">(sin dejar de comer rico)</span>
          </h1>

          <a href={BUY_LINK} onClick={handleCheckoutClick} className="block cursor-pointer hover:opacity-95 transition-opacity" data-id="element-20">
            <img src="https://cdn.magicpatterns.com/uploads/9ZGe6a4vWmfsr1ZcXGUF6W/437.webp" alt="Ebook 1000 Recetas Air Fryer" className="w-full max-w-md mx-auto rounded-xl shadow-2xl mb-8 border border-gray-200" data-id="element-21" />
          </a>

          <h2 className="text-xl md:text-2xl font-bold text-gray-700 mb-8 max-w-3xl mx-auto leading-relaxed" data-id="element-22">
            👉 Más de 1000 recetas en air fryer, fáciles, rápidas y pensadas
            para que no abandones nunca más
          </h2>

          <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 md:p-8 max-w-2xl mx-auto mb-10 text-left shadow-sm" data-id="element-23">
            <ul className="space-y-4 text-lg md:text-xl font-medium text-gray-800" data-id="element-24">
              <li className="flex items-center gap-3" data-id="element-25">
                <CheckCircle2 className="text-green-500 flex-shrink-0" size={28} data-id="element-26" />
                <span data-id="element-27">Comidas listas en minutos</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-28">
                <CheckCircle2 className="text-green-500 flex-shrink-0" size={28} data-id="element-29" />
                <span data-id="element-30">Sin dietas estrictas</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-31">
                <CheckCircle2 className="text-green-500 flex-shrink-0" size={28} data-id="element-32" />
                <span data-id="element-33">Sin pasar hambre</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-34">
                <CheckCircle2 className="text-green-500 flex-shrink-0" size={28} data-id="element-35" />
                <span data-id="element-36">Ideal si tenés poco tiempo</span>
              </li>
            </ul>
          </div>

          <div className="mb-10" data-id="element-37">
            <div className="flex items-center justify-center gap-4 mb-2" data-id="element-38">
              <span className="text-3xl md:text-4xl text-gray-400 line-through font-bold" data-id="element-39">
                $49.990
              </span>
              <span className="text-5xl md:text-7xl font-black text-red-600" data-id="element-40">
                👉 $14.990
              </span>
            </div>
            <div className="inline-block bg-red-100 text-red-800 font-bold px-4 py-1 rounded-full mb-2 animate-pulse" data-id="element-41">
              HOY SOLO
            </div>
            <p className="text-gray-500 italic font-medium mt-2" data-id="element-42">
              Menos de lo que gastás en una comida
            </p>
          </div>

          <CTAButton data-id="element-43" />
        </div>
      </section>

      {/* PROBLEMA SECTION */}
      <section className="bg-black text-white py-16 px-4" data-id="element-44">
        <div className="max-w-3xl mx-auto" data-id="element-45">
          <h2 className="text-3xl md:text-5xl font-black mb-8 text-center" data-id="element-46">
            🧠 Seamos honestos…
          </h2>

          <p className="text-xl md:text-2xl font-medium mb-8 text-center text-gray-300" data-id="element-47">
            Intentaste hacer dieta mil veces Y siempre pasa lo mismo:
          </p>

          <div className="space-y-6 text-xl md:text-2xl font-bold mb-12 bg-gray-900 p-8 rounded-2xl border border-gray-800" data-id="element-48">
            <div className="flex items-start gap-4" data-id="element-49">
              <XCircle className="text-red-500 flex-shrink-0 mt-1" size={32} data-id="element-50" />
              <p data-id="element-51">Te aburrís de comer siempre lo mismo</p>
            </div>
            <div className="flex items-start gap-4" data-id="element-52">
              <XCircle className="text-red-500 flex-shrink-0 mt-1" size={32} data-id="element-53" />
              <p data-id="element-54">Terminás con hambre</p>
            </div>
            <div className="flex items-start gap-4" data-id="element-55">
              <XCircle className="text-red-500 flex-shrink-0 mt-1" size={32} data-id="element-56" />
              <p data-id="element-57">Dejás todo a los pocos días</p>
            </div>
          </div>

          <p className="text-2xl md:text-3xl font-bold text-center mb-8" data-id="element-58">
            No es falta de disciplina…
          </p>

          <div className="text-center text-2xl md:text-4xl font-black leading-tight text-yellow-300" data-id="element-59">
            👉 Es que nadie te enseñó cómo comer rico y bajar de peso al mismo
            tiempo
          </div>
        </div>
      </section>

      {/* SOLUCIÓN SECTION */}
      <section className="bg-white py-16 px-4 border-b border-gray-200" data-id="element-60">
        <div className="max-w-4xl mx-auto text-center" data-id="element-61">
          <p className="text-2xl font-bold text-gray-500 mb-4 uppercase tracking-widest" data-id="element-62">
            💡 Por eso creamos esto:
          </p>
          <h2 className="text-4xl md:text-5xl font-black mb-8 text-blue-900" data-id="element-63">
            👉 "Bajá de peso comiendo así todos los días"
          </h2>

          <p className="text-xl md:text-2xl font-medium text-gray-700 mb-10 max-w-2xl mx-auto" data-id="element-64">
            Un ebook con más de 1000 recetas en air fryer, pensado para que:
          </p>

          <div className="grid md:grid-cols-3 gap-6 mb-12" data-id="element-65">
            <div className="bg-blue-50 p-6 rounded-xl border border-blue-100" data-id="element-66">
              <CheckCircle2 className="text-blue-600 mx-auto mb-4" size={40} data-id="element-67" />
              <p className="font-bold text-xl text-blue-900" data-id="element-68">
                Disfrutes la comida
              </p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl border border-blue-100" data-id="element-69">
              <CheckCircle2 className="text-blue-600 mx-auto mb-4" size={40} data-id="element-70" />
              <p className="font-bold text-xl text-blue-900" data-id="element-71">
                No sientas que estás a dieta
              </p>
            </div>
            <div className="bg-blue-50 p-6 rounded-xl border border-blue-100" data-id="element-72">
              <CheckCircle2 className="text-blue-600 mx-auto mb-4" size={40} data-id="element-73" />
              <p className="font-bold text-xl text-blue-900" data-id="element-74">
                Puedas mantenerlo en el tiempo
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECCIÓN VISUAL */}
      <section className="bg-gray-100 py-16 px-4" data-id="element-75">
        <div className="max-w-5xl mx-auto text-center" data-id="element-76">
          <h2 className="text-3xl md:text-4xl font-black mb-10" data-id="element-77">
            👉 Todo esto podés comer…{' '}
            <span className="text-green-600" data-id="element-78">y aun así bajar de peso</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12" data-id="element-79">
            <a href={BUY_LINK} onClick={handleCheckoutClick} className="block overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-shadow border-4 border-white" data-id="element-80">
              <img src="https://images.unsplash.com/photo-1604908176997-125f25cc6f3d?auto=format&fit=crop&w=800&q=80" alt="Pollo frito en air fryer" className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500" data-id="element-81" />
            </a>
            <a href={BUY_LINK} onClick={handleCheckoutClick} className="block overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-shadow border-4 border-white" data-id="element-82">
              <img src="https://images.unsplash.com/photo-1628840042765-356cda07504e?auto=format&fit=crop&w=800&q=80" alt="Papas fritas saludables" className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500" data-id="element-83" />
            </a>
          </div>

          <CTAButton text="👉 SÍ, QUIERO COMER ASÍ" data-id="element-84" />
        </div>
      </section>

      {/* BENEFICIOS */}
      <section className="bg-white py-16 px-4" data-id="element-85">
        <div className="max-w-3xl mx-auto" data-id="element-86">
          <h2 className="text-4xl md:text-5xl font-black mb-10 text-center" data-id="element-87">
            🎯 BENEFICIOS
          </h2>
          <p className="text-2xl font-bold text-center mb-8 text-gray-700" data-id="element-88">
            Con este ebook vas a poder:
          </p>

          <div className="space-y-4 text-lg md:text-xl font-bold text-gray-800 bg-gray-50 p-8 rounded-2xl border border-gray-200 shadow-sm" data-id="element-89">
            {['Saber exactamente qué cocinar cada día', 'Evitar caer en comida chatarra', 'Ahorrar tiempo en la cocina', 'Comer rico sin culpa', 'Mantener resultados reales'].map((benefit, i) => <div key={i} className="flex items-center gap-4 bg-white p-4 rounded-lg border border-gray-100 shadow-sm" data-id="element-90">
                <CheckCircle2 className="text-green-500 flex-shrink-0" size={28} data-id="element-91" />
                <span data-id="element-92">{benefit}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* BONOS */}
      <section className="bg-[#fffbeb] py-16 px-4 border-y-4 border-yellow-400" data-id="element-93">
        <div className="max-w-5xl mx-auto" data-id="element-94">
          <div className="text-center mb-12" data-id="element-95">
            <h2 className="text-4xl md:text-6xl font-black text-red-600 mb-4" data-id="element-96">
              🎁 BONOS (GRATIS HOY)
            </h2>
            <p className="text-2xl font-bold text-gray-800" data-id="element-97">
              Además HOY te llevás:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-16" data-id="element-98">
            {/* Bono 1 */}
            <div className="bg-white p-8 rounded-2xl shadow-xl border-2 border-yellow-200 relative" data-id="element-99">
              <div className="absolute -top-4 -right-4 bg-red-600 text-white font-black px-4 py-2 rounded-full transform rotate-12 shadow-lg" data-id="element-100">
                VALOR: $15.000 (HOY GRATIS)
              </div>
              <a href={BUY_LINK} onClick={handleCheckoutClick} className="block mb-6" data-id="element-101">
                <img src="https://cdn.magicpatterns.com/uploads/d3h5dMDXGJxkWaWx6PFpCb/mock_l1.webp" alt="Plan de comidas de 7 días" className="w-full max-w-[300px] mx-auto rounded-lg shadow-md hover:scale-105 transition-transform" data-id="element-102" />
              </a>
              <h3 className="text-2xl font-black mb-4 text-center" data-id="element-103">
                🎁 BONO 1: Plan de comidas de 7 días para bajar de peso
              </h3>
              <p className="text-lg text-gray-600 text-center font-medium" data-id="element-104">
                👉 Sabés exactamente qué comer sin pensar
              </p>
            </div>

            {/* Bono 2 */}
            <div className="bg-white p-8 rounded-2xl shadow-xl border-2 border-yellow-200 relative" data-id="element-105">
              <div className="absolute -top-4 -right-4 bg-red-600 text-white font-black px-4 py-2 rounded-full transform rotate-12 shadow-lg" data-id="element-106">
                VALOR: $10.000 (HOY GRATIS)
              </div>
              <a href={BUY_LINK} onClick={handleCheckoutClick} className="block mb-6" data-id="element-107">
                <img src="https://cdn.magicpatterns.com/uploads/aEpY4A61SAawmB1qipz1m2/mock_l2.webp" alt="Guía rápida air fryer" className="w-full max-w-[300px] mx-auto rounded-lg shadow-md hover:scale-105 transition-transform" data-id="element-108" />
              </a>
              <h3 className="text-2xl font-black mb-4 text-center" data-id="element-109">
                🎁 BONO 2: Guía rápida: cómo usar tu air fryer para cocinar todo
              </h3>
              <p className="text-lg text-gray-600 text-center font-medium" data-id="element-110">
                👉 Ideal si recién empezás
              </p>
            </div>
          </div>

          <CTAButton text="👉 QUIERO MIS BONOS GRATIS" data-id="element-111" />
        </div>
      </section>

      {/* PRUEBA SOCIAL */}
      <section className="bg-white py-16 px-4" data-id="element-112">
        <div className="max-w-4xl mx-auto" data-id="element-113">
          <h2 className="text-4xl md:text-5xl font-black mb-12 text-center" data-id="element-114">
            💬 PRUEBA SOCIAL
          </h2>

          <div className="grid gap-6 md:grid-cols-2 mb-12" data-id="element-115">
            {[{
            text: 'No pensé que iba a funcionar… pero bajé 3kg en semanas comiendo esto',
            author: 'Mariana, 34 años'
          }, {
            text: 'Por primera vez no dejé una dieta… porque ni parece dieta',
            author: 'Carla, 29 años'
          }, {
            text: 'La air fryer me cambió todo, es súper fácil',
            author: 'Sofía, 41 años'
          }, {
            text: 'Increíble la cantidad de recetas. Mi familia ni se da cuenta que estamos comiendo sano.',
            author: 'Romina, 38 años'
          }, {
            text: "Bajé 5 kilos en un mes y sigo comiendo 'frito'. La mejor inversión.",
            author: 'Valeria, 45 años'
          }].map((testimonial, i) => <div key={i} className={`bg-gray-50 p-6 rounded-xl border border-gray-200 shadow-sm ${i === 4 ? 'md:col-span-2 md:max-w-xl md:mx-auto' : ''}`} data-id="element-116">
                <div className="flex gap-1 text-yellow-400 mb-3" data-id="element-117">
                  {[...Array(5)].map((_, j) => <Star key={j} fill="currentColor" size={20} data-id="element-118" />)}
                </div>
                <p className="text-lg font-bold italic text-gray-800 mb-4" data-id="element-119">
                  👉 "{testimonial.text}"
                </p>
                <p className="text-gray-500 font-medium" data-id="element-120">
                  — {testimonial.author}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* URGENCIA & OFERTA FINAL */}
      <section className="bg-black text-white py-16 px-4 border-t-8 border-red-600" data-id="element-121">
        <div className="max-w-4xl mx-auto text-center" data-id="element-122">
          <div className="mb-12" data-id="element-123">
            <h2 className="text-4xl md:text-6xl font-black text-red-500 mb-6 flex items-center justify-center gap-4" data-id="element-124">
              <span className="animate-pulse" data-id="element-125">⚠️</span> URGENCIA{' '}
              <span className="animate-pulse" data-id="element-126">⚠️</span>
            </h2>
            <p className="text-2xl md:text-3xl font-bold mb-4" data-id="element-127">
              Este contenido no va a estar disponible a este precio por mucho
              tiempo
            </p>
            <p className="text-xl text-gray-400 font-medium" data-id="element-128">
              👉 Es una oferta especial por tiempo limitado
            </p>
          </div>

          <div className="bg-white text-black rounded-3xl p-8 md:p-12 shadow-2xl max-w-3xl mx-auto border-4 border-yellow-400 relative" data-id="element-129">
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-black font-black px-8 py-2 rounded-full text-xl shadow-lg border-2 border-white" data-id="element-130">
              💰 OFERTA FINAL
            </div>

            <h3 className="text-2xl md:text-3xl font-black mb-6 text-gray-800 mt-4" data-id="element-131">
              HOY ACCEDÉS A TODO POR:
            </h3>

            <div className="flex flex-col items-center justify-center gap-2 mb-8" data-id="element-132">
              <span className="text-3xl text-gray-400 line-through font-bold" data-id="element-133">
                $49.990
              </span>
              <span className="text-6xl md:text-8xl font-black text-red-600" data-id="element-134">
                👉 $14.990
              </span>
              <p className="text-gray-500 italic font-bold mt-2" data-id="element-135">
                Menos de lo que gastás en una comida
              </p>
            </div>

            <div className="text-left max-w-md mx-auto mb-10 space-y-3" data-id="element-136">
              <div className="flex items-center gap-3 text-lg font-bold" data-id="element-137">
                <CheckCircle2 className="text-green-500" size={24} data-id="element-138" />
                <span data-id="element-139">Ebook principal (+1000 recetas)</span>
              </div>
              <div className="flex items-center gap-3 text-lg font-bold" data-id="element-140">
                <CheckCircle2 className="text-green-500" size={24} data-id="element-141" />
                <span data-id="element-142">
                  Plan de 7 días <span className="text-red-500" data-id="element-143">(GRATIS)</span>
                </span>
              </div>
              <div className="flex items-center gap-3 text-lg font-bold" data-id="element-144">
                <CheckCircle2 className="text-green-500" size={24} data-id="element-145" />
                <span data-id="element-146">
                  Guía air fryer <span className="text-red-500" data-id="element-147">(GRATIS)</span>
                </span>
              </div>
            </div>

            <CTAButton text="👉 QUIERO BAJAR DE PESO COMIENDO RICO" data-id="element-148" />

            <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm font-bold text-gray-600" data-id="element-149">
              <div className="flex items-center gap-2" data-id="element-150">
                <CheckCircle2 size={18} className="text-green-500" data-id="element-151" />
                Acceso inmediato
              </div>
              <div className="flex items-center gap-2" data-id="element-152">
                <CheckCircle2 size={18} className="text-green-500" data-id="element-153" />
                Formato digital
              </div>
              <div className="flex items-center gap-2" data-id="element-154">
                <CheckCircle2 size={18} className="text-green-500" data-id="element-155" />
                Lo recibís en segundos
              </div>
            </div>
          </div>

          {/* SEGURIDAD */}
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-gray-300 font-medium" data-id="element-156">
            <div className="flex items-center gap-2" data-id="element-157">
              <ShieldCheck size={24} className="text-green-400" data-id="element-158" />
              🔒 Compra 100% segura
            </div>
            <div className="flex items-center gap-2" data-id="element-159">
              <Mail size={24} className="text-blue-400" data-id="element-160" />
              📩 Acceso inmediato por email
            </div>
            <div className="flex items-center gap-2" data-id="element-161">
              <Smartphone size={24} className="text-purple-400" data-id="element-162" />
              💻 Compatible con celular y PC
            </div>
          </div>
        </div>
      </section>

      <PurchasePopup data-id="element-163" />
    </div>;
}